import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import classnames from 'classnames';
import Logo from './components/Logo';
import LinkSelector from '../../../Header/LinkSelector';
import { Offcanvas } from '../../../Offcanvas/Offcanvas';
import { CategoryLink, InternalLink } from '../../../form-elements/Link';
import Button from '../../../form-elements/Button';
import { categoriesSelector } from '../../../../../modules/selectors/standort/categories';
import { categoryUrlSelector, standortUrlSelector } from '../../../../../modules/selectors/url';
import ShopMenuList from '../../../Header/ShopMenuList';
import { carDealerLinkSelector } from '../../../../../modules/selectors/standort/theme';
import HolidayBanner from '../../../Header/HolidayBanner';
import loadable from '@loadable/component';
import { markenStandortSelector } from '../../../../../modules/selectors/standort/markenStandort';
import { apiClient } from '../../../../../helpers/apiHelperV2';
import useSWR from 'swr';
import { saveCouponBannerHeight } from '../../../../../modules/actions/couponBannerHeight';
import { setServiceNavigatorStatus } from '../../../../../modules/actions/standort/service/serviceNavigator';
import useWindowSize from '../../../../../helpers/windowHelper';
import { MobileMenu } from '../../../MobileMenu/MobileMenu';


const CouponBanner = loadable(() => import('../../../CouponBanner/Couponbanner'));

function AutoschmittHeader({ links }) {
    const categories = useSelector(categoriesSelector);
    const categoryUrl = useSelector(categoryUrlSelector);
    const carDealerLink = useSelector(carDealerLinkSelector);
    const [isNavSidebarVisible, setIsNavSidebarVisible] = useState(false);
    const markenStandort = useSelector(markenStandortSelector);
    const standortUrl = useSelector(standortUrlSelector);
    const coupons = useSelector(state => state.standort[standortUrl].coupons);
    const [couponBannerActive, setCouponBannerActive] = useState(false);
    const dispatch = useDispatch();
    const focusBtnRef = useRef();

    const { width } = useWindowSize();
    const BREAKPOINT_SMALL = 640

    const closeSidebar = useCallback(() => {
        setIsNavSidebarVisible(false);
    }, []);

    const openSidebar = useCallback(() => {
        setIsNavSidebarVisible(true);
    }, []);

    const { data: serviceNavigatorstatus } = useSWR(`/api/v1/getServicenavigatorStatus?standortId=${markenStandort._id}`, async url => {
        const { data } = await apiClient.get(url);
        return data;
    });

    const serviceNavigatorStatus = useMemo(() => serviceNavigatorstatus?.status ?? [], [serviceNavigatorstatus]);

    if (serviceNavigatorStatus !== undefined) {
        dispatch(setServiceNavigatorStatus(serviceNavigatorStatus));
    } 

    const filterSpecialCategories = useCallback((categoryList) => {
        if (!categoryList) return [];
        
        return categoryList
            .filter(cat => ('isSpecial' in cat && !cat.isSpecial) || !('isSpecial' in cat))
            .map(cat => {
                if (cat.subCategories && cat.subCategories.length > 0) {
                    return {
                        ...cat,
                        subCategories: filterSpecialCategories(cat.subCategories)
                    };
                }
                return cat;
            });
    }, []);
    
    const filteredCategories = useMemo(() => {
        if (!categories) return [];
        if('showSpecialCategories' in markenStandort && !markenStandort.showSpecialCategories) {
            return categories;
        }
        return filterSpecialCategories(categories);
    }, [categories, markenStandort, filterSpecialCategories]);

    const { reparaturUndWartungCategory, categoriesWithoutReparaturUndWartung } = useMemo(() => {
        if (!serviceNavigatorStatus) {
            const [reparaturUndWartungCategory, categoriesWithoutReparaturUndWartung] = _.partition(filteredCategories, { url: 'reparatur-wartung' });
            return { reparaturUndWartungCategory, categoriesWithoutReparaturUndWartung };
        } else {
            const [reparaturUndWartungCategory, categoriesWithoutReparaturUndWartung] = _.partition(filteredCategories, { url: '' });
            return { reparaturUndWartungCategory, categoriesWithoutReparaturUndWartung };
        }

    }, [filteredCategories, serviceNavigatorStatus]);

    const isLinkActive = category => {
        return category.url === categoryUrl || category.subCategories.find(subCategory => subCategory.url === categoryUrl);
    };

    const checkIfCouponBannerActive = () => {
        return coupons?.data.some(coupon => { return coupon.banner.isActive === true})
    }

    useEffect(() => {
        let isActive = checkIfCouponBannerActive();
        setCouponBannerActive(isActive);
        dispatch(saveCouponBannerHeight(isActive ? 36 : 0));
    },[coupons]);
    useEffect(() => {
        if (isNavSidebarVisible && focusBtnRef.current) {
          focusBtnRef.current.focus();
        }
      }, [isNavSidebarVisible]);

      let handlePress = (e) =>{
        e.preventDefault()
        if(e.key == "Enter"){
            closeSidebar()
        }
    }

    return (
        <>
            <div className="uk-sticky-fixed uk-width-1-1" style={{ height:  couponBannerActive ? 106 : 70, position: 'sticky', top: 0 }}>
                <div className="uk-navbar-container uk-navbar-sticky">
                    <div className="uk-container">
                        <div className="uk-flex uk-flex-middle uk-flex-between" style={{ marginTop: 10, marginBottom: 10}}>
                            <div className="uk-width-auto" style={{ maxWidth: '100%' }}>
                                <Logo />
                            </div>
                            <nav className="uk-navbar" data-uk-navbar="" aria-label='Hauptmenü'>
                                <div className="uk-navbar-right">
                                    <Button
                                        className="uk-navbar-item uk-text-primary uk-text-bold"
                                        modifier="link"
                                        onClick={openSidebar}
                                        ariaLabel="Menü öffnen"
                                    >
                                        <span className="uk-flex uk-flex-column">
                                            <i data-uk-navbar-toggle-icon="ratio: 1.7" aria-hidden="true"/>
                                            <span className="uk-text-uppercase uk-text-light">Menü</span>
                                        </span>
                                    </Button>
                                </div>
                            </nav>
                        </div>
                    </div>
                </div>
                <HolidayBanner />
                <CouponBanner />
            </div>
            <Offcanvas overlay isVisible={width && width > BREAKPOINT_SMALL && isNavSidebarVisible} onIsVisibleChange={visible => setIsNavSidebarVisible(visible)} flip={true}>
                <div className="uk-offcanvas-bar">
                    <button className="uk-offcanvas-close focus" type="button" ref={focusBtnRef} data-uk-close="" onKeyPress={handlePress} onClick={closeSidebar} aria-label='Navigation schließen' />
                    <span className='uk-text-bold menu-black'><strong style={{'font-weight': "bold !important"}}>Menü</strong></span>
                    <ShopMenuList categories={categoriesWithoutReparaturUndWartung} closeSidebar={closeSidebar} data-uk-nav="" />
                    <ul className="uk-nav-default uk-nav-parent-icon uk-margin-top" data-uk-nav="">
                        {reparaturUndWartungCategory.map(category => (
                            <li key={category._id} className={classnames({ 'uk-active': isLinkActive(category) })} onClick={closeSidebar}>
                                <strong>
                                    <CategoryLink category={category} className="uk-text-bold strong-text">
                                        {category.title}
                                    </CategoryLink>
                                </strong>
                            </li>
                        ))}
                        <li>
                            <InternalLink to="/scheibenreparatur">
                                <p onClick={closeSidebar}>Scheibenreparatur</p>
                            </InternalLink>
                        </li>
                    </ul>
                    {serviceNavigatorStatus && (
                        <ul className="uk-nav-default uk-nav-parent-icon uk-margin-top" data-uk-nav="">
                            <li onClick={closeSidebar}>
                                <strong>
                                    <LinkSelector
                                        to='/servicenavigator'
                                        label='Service-Navigator'
                                        className="uk-text-bold strong-text"
                                        type='internalLink'
                                    />
                                </strong>
                            </li>
                        </ul>
                    )}
                    {!_.isEmpty(links) && (
                        <div className="uk-margin-large-top">
                            <LinkSelector to={carDealerLink.to} type={carDealerLink.type} className="strong-text" label={<h2><strong>{carDealerLink.label}</strong></h2>} />
                            <ul className="uk-nav-default uk-nav-parent-icon" data-uk-nav="">
                                {links.map((item, index) => (
                                    <li key={index}>
                                        <strong>
                                            <LinkSelector
                                                to={item.to}
                                                action={item.action}
                                                category={item.category}
                                                label={item.label}
                                                type={item.type}
                                                className="strong-text"
                                            />
                                        </strong>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </Offcanvas>
            <Offcanvas isVisible={width && width <= BREAKPOINT_SMALL && isNavSidebarVisible} onIsVisibleChange={visible => setIsNavSidebarVisible(visible)}>
                <div className="uk-offcanvas-bar mobile-full-modal">
                    <div style={{padding: 15}}>
                        <button className="uk-offcanvas-close focus" type="button" ref={focusBtnRef} data-uk-close="" onKeyPress={handlePress} onClick={closeSidebar} aria-label='Navigation schließen' />
                        <span className='uk-text-bold menu-black'><strong style={{fontWeight: "bold !important"}}>Menü</strong></span>
                    </div>
                    <MobileMenu markenStandort={markenStandort} categories={[...categoriesWithoutReparaturUndWartung, ...reparaturUndWartungCategory]} setIsNavSidebarVisible={setIsNavSidebarVisible}>                
                    <>
                            
                            {serviceNavigatorStatus && (
                                <li className="mobile-menu-category" onClick={closeSidebar}>
                                    <strong>
                                        <LinkSelector
                                            to='/servicenavigator'
                                            label='Service-Navigator'
                                            className="uk-text-bold strong-text"
                                            type='internalLink'
                                        />
                                    </strong>
                                </li>
                            )}
                            {!_.isEmpty(links) && (
                                <>
                                    <li className="mobile-menu-category">
                                        <LinkSelector to={carDealerLink.to} type={carDealerLink.type} className="strong-text" label={<h2><strong>{carDealerLink.label}</strong></h2>} />
                                    </li>
                                    {links.map((item, index) => (
                                        <li className="mobile-menu-category" key={index}>
                                            <strong>
                                                <LinkSelector
                                                    to={item.to}
                                                    action={item.action}
                                                    category={item.category}
                                                    label={item.label}
                                                    type={item.type}
                                                    className="strong-text"
                                                />
                                            </strong>
                                        </li>
                                    ))}
                                </>
                            )} 
                        </>
                    </MobileMenu>
                </div>
                
            </Offcanvas>
        </>
    );
}

export default AutoschmittHeader;
